import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  ExternalLink, Users, TrendingUp, DollarSign, Lock,
  Mic, Share2, BarChart3, Crown, Gift, Radio, Play,
} from 'lucide-react';

const ProductsBentoSection = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section ref={ref} className="section-padding">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center max-w-2xl mx-auto mb-8 md:mb-10"
        >
          <span className="text-xs sm:text-sm text-[var(--accent-primary)] uppercase tracking-widest font-medium">Sponsor Perks</span>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mt-3 mb-3">
            Exclusive Access to Our
            <span className="text-[var(--accent-primary)]"> Product Suite</span>
          </h2>
          <p className="text-xs sm:text-sm text-[var(--text-secondary)]">
            All sponsors get free access to these powerful tools
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">

          {/* TrueKOL - Featured, takes 2 columns on lg */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-[var(--bg-secondary)] to-[rgba(168,85,247,0.05)] border border-purple-500/20 hover:border-purple-500/40 transition-colors"
          >
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-base sm:text-lg">TrueKOL</h3>
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                        Invite Only
                      </span>
                    </div>
                    <p className="text-[10px] sm:text-xs text-[var(--text-muted)]">Vetted KOL Network · 2,500+ creators</p>
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-[var(--text-secondary)] mb-4">
                  Direct access to a vetted network of 2,500+ crypto KOLs and creators. Skip the middlemen, negotiate directly, and run campaigns with verified performance data.
                </p>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="flex items-center gap-2 text-[10px] sm:text-xs text-[var(--text-muted)]">
                    <Users className="w-3.5 h-3.5 text-purple-400" />
                    <span>2,500+ vetted KOLs</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] sm:text-xs text-[var(--text-muted)]">
                    <TrendingUp className="w-3.5 h-3.5 text-purple-400" />
                    <span>Real engagement data</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] sm:text-xs text-[var(--text-muted)]">
                    <DollarSign className="w-3.5 h-3.5 text-purple-400" />
                    <span>Transparent pricing</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] sm:text-xs text-[var(--text-muted)]">
                    <Lock className="w-3.5 h-3.5 text-purple-400" />
                    <span>Contact directly</span>
                  </div>
                </div>

                <a href="/truekol" className="inline-flex items-center gap-1.5 text-xs text-purple-400 hover:underline">
                  Request early access
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <div className="sm:w-40 p-3 sm:p-4 rounded-xl bg-[var(--bg-tertiary)] border border-[var(--border-subtle)]">
                <div className="flex items-center gap-1.5 mb-2">
                  <Gift className="w-3.5 h-3.5 text-purple-400" />
                  <span className="text-[10px] sm:text-xs font-medium">Sponsor Perk</span>
                </div>
                <p className="text-[9px] sm:text-[10px] text-[var(--text-muted)]">
                  Save <span className="text-purple-400 font-medium">$1,000s/year</span> on KOL campaigns with exclusive member rates
                </p>
              </div>
            </div>
          </motion.div>

          {/* Clips - corrected copy · CPM-based short-form distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15 }}
            className="p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-[var(--bg-secondary)] to-[rgba(34,197,94,0.05)] border border-green-500/20 hover:border-green-500/40 transition-colors"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <Play className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-base sm:text-lg">Clips</h3>
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                    Live · 1B+ views
                  </span>
                </div>
                <p className="text-[10px] sm:text-xs text-[var(--text-muted)]">Short-form Distribution</p>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-[var(--text-secondary)] mb-4">
              Founder-led short-form content, created and distributed where real attention lives. Creator collabs, expert editing, and multi-platform reach across TikTok, Reels, Shorts, and X.
            </p>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-[10px] sm:text-xs text-[var(--text-muted)]">
                <Mic className="w-3.5 h-3.5 text-green-400" />
                <span>Web3 podcaster &amp; AMA network</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] sm:text-xs text-[var(--text-muted)]">
                <Share2 className="w-3.5 h-3.5 text-green-400" />
                <span>Multi-platform distribution</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] sm:text-xs text-[var(--text-muted)]">
                <BarChart3 className="w-3.5 h-3.5 text-green-400" />
                <span>Real-time performance analytics</span>
              </div>
            </div>

            <a
              href="/clips"
              className="inline-flex items-center gap-1.5 text-xs text-green-400 hover:underline"
            >
              Explore Clips
              <ExternalLink className="w-3 h-3" />
            </a>
          </motion.div>
        </div>

        {/* Disclaimer below bento */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.4 }}
          className="text-[11px] leading-relaxed sm:text-xs sm:leading-relaxed text-[var(--text-muted)] italic text-center mt-6 sm:mt-8 max-w-3xl mx-auto px-2"
        >
          Perks are complimentary bonuses offered alongside sponsorship and may evolve as our products grow. Your sponsorship is only limited to the LayerSeek DeFi edition virtual event · everything else is extra bonuses we&apos;re excited to share.
        </motion.p>
      </div>
    </section>
  );
};

export default ProductsBentoSection;
